/**
 * Spec D3 — actor evidence view (Layers A/B/C, AC-D3-1/2/3).
 *
 * Layer A: per-thesis donor shares from the actor-contribution endpoint
 * (display metric — weight-sum share, not the identification likelihood).
 * Layer B: what-if slider — every projection is the REAL deterministic
 * downstream re-run server-side (AC-D3-2), never a client approximation.
 * Layer C: trace to strategy citations with before-paywall (critical) badges,
 * rendered when the caller provides the strategy's reverse index.
 *
 * D53: the model-suggested weight sentence comes from the server (`suggestion`)
 * and is exposure-only (P3) — [Apply] just moves the slider; persisting a
 * weight stays the operator's explicit act on the Actors tab / managed CLI.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Slider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { getAuth } from 'firebase/auth';
import type { CliProductInsightActorContributionData } from '@modules/shared-types';

function getBaseUrl(): string {
  if (import.meta.env.VITE_BUILDER_API_URL) return import.meta.env.VITE_BUILDER_API_URL;
  if (import.meta.env.VITE_AGENT_API_URL) return import.meta.env.VITE_AGENT_API_URL;
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  if (hostname === 'localhost' || hostname === '127.0.0.1') return 'http://localhost:8081';
  if (hostname === 'dev.segmently.ai' || hostname.includes('devsegmentlyai')) return 'https://dev-api.segmently.ai';
  if (hostname === 'stage.segmently.ai' || hostname.includes('stagesegmentlyai')) return 'https://stage-api.segmently.ai';
  return 'https://api.segmently.ai';
}

/** Minimal slice of the strategy reverse index the Layer C trace reads. */
export interface ActorEvidenceTraceIndex {
  entries: Array<{
    insightId: string;
    citations: Array<{ blockId?: string; blockName?: string; screenId?: string; variableName?: string; via?: string }>;
  }>;
  /** insightIds cited strictly before the value point (= before paywall). */
  criticalInsightIds?: string[];
}

export interface ActorEvidenceDialogProps {
  open: boolean;
  onClose: () => void;
  projectId: string;
  /** Analysis to read; the actor must be in its frozen input snapshot. */
  analysisId: string;
  actorId: string;
  actorName?: string;
  /** Proposal-row magnitude (0..1) when the entry point carries one (D53). */
  estimate?: number;
  /** Strategy reverse index → renders the Layer C citation trace. */
  traceIndex?: ActorEvidenceTraceIndex;
}

export const ActorEvidenceDialog: React.FC<ActorEvidenceDialogProps> = ({
  open,
  onClose,
  projectId,
  analysisId,
  actorId,
  actorName,
  estimate,
  traceIndex,
}) => {
  const [data, setData] = useState<CliProductInsightActorContributionData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sliderWeight, setSliderWeight] = useState<number | null>(null);

  const fetchContribution = useCallback(async (whatIfWeight?: number) => {
    setLoading(true);
    setError(null);
    try {
      const token = await getAuth().currentUser?.getIdToken();
      if (!token) throw new Error('User not authenticated');
      const params = new URLSearchParams({ actorId });
      if (whatIfWeight !== undefined) params.set('whatIfWeight', String(whatIfWeight));
      if (estimate !== undefined) params.set('estimate', String(estimate));
      const response = await fetch(
        `${getBaseUrl()}/api/cli/v1/projects/${encodeURIComponent(projectId)}/product/insights/${encodeURIComponent(analysisId)}/actor-contribution?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload?.error?.message || payload?.detail || `HTTP ${response.status}`);
      }
      setData(payload.data as CliProductInsightActorContributionData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load actor contribution');
    } finally {
      setLoading(false);
    }
  }, [projectId, analysisId, actorId, estimate]);

  useEffect(() => {
    if (!open) return;
    setData(null);
    setSliderWeight(null);
    void fetchContribution();
  }, [open, fetchContribution]);

  const currentWeight = data?.actor.weight ?? 3;
  const effectiveSlider = sliderWeight ?? currentWeight;

  const traceRows = useMemo(() => {
    if (!traceIndex || !data) return [];
    const critical = new Set(traceIndex.criticalInsightIds ?? []);
    const byInsight = new Map(traceIndex.entries.map((entry) => [entry.insightId, entry.citations]));
    return data.shares
      .filter((share) => byInsight.has(share.insightId))
      .map((share) => ({
        share,
        critical: critical.has(share.insightId),
        citations: (byInsight.get(share.insightId) ?? []).slice(0, 3),
      }));
  }, [traceIndex, data]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth data-testid="actor-evidence-dialog">
      <DialogTitle>Actor evidence — {data?.actor.name ?? actorName ?? actorId}</DialogTitle>
      <DialogContent>
        {loading && !data && (
          <Stack alignItems="center" sx={{ py: 3 }}><CircularProgress size={28} /></Stack>
        )}
        {error && <Alert severity="error" data-testid="actor-evidence-error">{error}</Alert>}

        {data && (
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <Box data-testid="actor-evidence-shares">
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="subtitle2">Contribution to theses</Typography>
                <Chip size="small" variant="outlined" label="Layer A · display metric" />
              </Stack>
              {data.shares.length === 0 ? (
                <Alert severity="info" sx={{ mt: 1 }}>
                  This actor contributed no source items to the aggregated theses of this analysis.
                </Alert>
              ) : (
                <Table size="small" sx={{ mt: 0.5 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Thesis</TableCell>
                      <TableCell align="right">This actor</TableCell>
                      <TableCell align="right">Top donor</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.shares.slice(0, 10).map((share) => (
                      <TableRow key={share.insightId} data-testid={`actor-evidence-share-${share.insightId}`}>
                        <TableCell>
                          <Typography variant="body2">{share.description}</Typography>
                          <Typography variant="caption" color="text.secondary">{share.category}</Typography>
                        </TableCell>
                        <TableCell align="right"><b>{Math.round(share.actorShare * 100)}%</b></TableCell>
                        <TableCell align="right">
                          <Typography variant="caption" color="text.secondary">
                            {share.topDonor ? `${share.topDonor.actorName} ${Math.round(share.topDonor.share * 100)}%` : '—'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
              {data.shares.length > 10 && (
                <Typography variant="caption" color="text.secondary">
                  Showing the top 10 of {data.shares.length} theses by share.
                </Typography>
              )}
            </Box>

            <Box data-testid="actor-evidence-whatif">
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="subtitle2">What-if: actor weight</Typography>
                <Chip size="small" variant="outlined" label="Layer B · real recompute" />
              </Stack>
              <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 0.5, px: 1 }}>
                <Typography variant="caption" color="text.secondary">1</Typography>
                <Slider
                  size="small"
                  min={1}
                  max={5}
                  step={1}
                  marks
                  value={effectiveSlider}
                  onChange={(_event, value) => setSliderWeight(value as number)}
                  onChangeCommitted={(_event, value) => {
                    if (value !== currentWeight) void fetchContribution(value as number);
                  }}
                  data-testid="actor-evidence-weight-slider"
                  sx={{ maxWidth: 320 }}
                />
                <Typography variant="caption" color="text.secondary">5</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 96 }}>
                  weight {currentWeight}{effectiveSlider !== currentWeight ? ` → ${effectiveSlider}` : ''}
                </Typography>
                {loading && <CircularProgress size={16} />}
              </Stack>

              <Alert severity="info" icon={false} sx={{ mt: 1 }} data-testid="actor-evidence-suggestion">
                💡 {data.suggestion.sentence}
                {data.suggestion.basis === 'estimate' && data.suggestion.suggestedWeight !== null
                  && data.suggestion.suggestedWeight !== effectiveSlider && (
                  <Button
                    size="small"
                    sx={{ ml: 1 }}
                    onClick={() => {
                      setSliderWeight(data.suggestion.suggestedWeight);
                      void fetchContribution(data.suggestion.suggestedWeight!);
                    }}
                    data-testid="actor-evidence-apply-suggestion"
                  >
                    Preview
                  </Button>
                )}
              </Alert>

              {data.whatIf && (
                <Table size="small" sx={{ mt: 1 }} data-testid="actor-evidence-deltas">
                  <TableHead>
                    <TableRow>
                      <TableCell>Projected change</TableCell>
                      <TableCell align="right">Baseline</TableCell>
                      <TableCell align="right">At weight {data.whatIf.overrides[actorId]}</TableCell>
                      <TableCell align="right">Δ</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.whatIf.priorShifts
                      .filter((shift) => shift.atomId === actorId || shift.atomId === data.actor.audienceId)
                      .slice(0, 1)
                      .map((shift) => (
                        <TableRow key={shift.atomId}>
                          <TableCell>Prior share — {data.actor.name}</TableCell>
                          <TableCell align="right">{Math.round(shift.baseline * 100)}%</TableCell>
                          <TableCell align="right">{Math.round(shift.projected * 100)}%</TableCell>
                          <TableCell align="right" sx={{ color: shift.delta < 0 ? 'error.main' : 'success.main' }}>
                            {shift.delta > 0 ? '+' : ''}{Math.round(shift.delta * 100)} pp
                          </TableCell>
                        </TableRow>
                      ))}
                    {data.whatIf.insightMetricDeltas.slice(0, 4).map((delta) => (
                      <TableRow key={delta.insightId}>
                        <TableCell>
                          <Typography variant="body2">Information gain — {delta.description}</Typography>
                        </TableCell>
                        <TableCell align="right">{delta.informationGain.baseline.toFixed(2)}</TableCell>
                        <TableCell align="right">{delta.informationGain.projected.toFixed(2)}</TableCell>
                        <TableCell align="right" sx={{ color: delta.informationGain.delta < 0 ? 'error.main' : 'success.main' }}>
                          {delta.informationGain.delta > 0 ? '+' : ''}{delta.informationGain.delta.toFixed(2)}
                        </TableCell>
                      </TableRow>
                    ))}
                    {data.whatIf.questionRankMoves.slice(0, 3).map((move) => (
                      <TableRow key={move.insightId}>
                        <TableCell>
                          <Typography variant="body2">Question rank — {move.questionText || move.insightId}</Typography>
                        </TableCell>
                        <TableCell align="right">#{move.baselineRank}</TableCell>
                        <TableCell align="right">#{move.projectedRank}</TableCell>
                        <TableCell align="right" sx={{ color: move.move > 0 ? 'error.main' : 'success.main' }}>
                          {move.move < 0 ? `▲ ${Math.abs(move.move)}` : `▼ ${move.move}`}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
              {data.whatIf && data.whatIf.insightMetricDeltas.length === 0 && data.whatIf.questionRankMoves.length === 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  No metric or question-rank changes at this weight.
                </Typography>
              )}
            </Box>

            {traceIndex && (
              <Box data-testid="actor-evidence-trace">
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="subtitle2">Trace to content</Typography>
                  <Chip size="small" variant="outlined" label="Layer C" />
                </Stack>
                {traceRows.length === 0 ? (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    None of this actor's theses are cited by the strategy.
                  </Typography>
                ) : traceRows.map(({ share, critical, citations }) => (
                  <Stack key={share.insightId} direction="row" spacing={1} alignItems="center" sx={{ mt: 0.75 }}>
                    <Typography variant="body2" sx={{ flex: 1 }}>
                      {citations[0]?.blockName ?? citations[0]?.blockId ?? 'Strategy'} — cites “{share.description}”
                    </Typography>
                    <Chip
                      size="small"
                      variant="outlined"
                      color={critical ? 'error' : 'default'}
                      label={critical ? 'before paywall' : 'after paywall'}
                    />
                  </Stack>
                ))}
              </Box>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={onClose} data-testid="actor-evidence-done">Done</Button>
      </DialogActions>
    </Dialog>
  );
};
